/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ReactNode, RefObject } from "react";
import { createRef, useEffect, useRef, useState } from "react";
import { useHover } from "react-aria";

import type { ThemeType } from "../../../../../global/emotion";
import { numericValues, radius, spacing } from "../../../../../global/emotion";
import type { CarouselApi } from "../../../../hooks/useCarousel";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { CarouselButton } from "./CarouselButton/CarouselButton.emotion";
import { CarouselItem } from "./CarouselItem/CarouselItem.emotion";
import { CarouselPageOffset } from "./CarouselPageOffset/CarouselPageOffset.emotion";

const carouselStyle: Interpolation = {
  overflow: "hidden",
  position: "relative",
  width: "100%",
};
const containerStyle: Interpolation = {
  WebkitOverflowScrolling: "touch",
  display: "flex",
  overflowX: "auto",
  overflowY: "visible",
  scrollBehavior: "smooth",
  scrollSnapType: "x mandatory",
  scrollbarWidth: "none",
  "::-webkit-scrollbar": {
    display: "none",
  },
};
const containerPageOffsetStyle: Interpolation<{
  pageOffset: CarouselApi["pageOffset"];
}> = (props) => ({
  scrollPaddingLeft: `${props.pageOffset[0][0]}px`,
  scrollPaddingRight: `${props.pageOffset[1][0]}px`,
  [cssMediaQueryMinWidthTablet]: {
    scrollPaddingLeft: `${props.pageOffset[0][1]}px`,
    scrollPaddingRight: `${props.pageOffset[1][1]}px`,
  },
});
const startStyle: Interpolation = {
  scrollSnapAlign: "end",
};
const pageButtonStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.higherContrastMedLow,
  borderRadius: radius.full,
  borderWidth: numericValues[0],
  height: spacing.xs,
  padding: numericValues[0],
  width: spacing.xs,
});
const activePageButtonStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.higherContrastHighest,
});
const pageButtonListStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: spacing.xxs,
  justifyContent: "center",
  paddingBlock: spacing.m,
  paddingInline: numericValues[0],
};

export type CarouselProps = HallowElementProps<"div"> & {
  carouselApi?: CarouselApi;
  containerCSS?: Interpolation;
  visibilityProps?: {
    onVisible: (childIdx: number) => void;
    viewportRef: RefObject<HTMLElement>;
    observerProps?: IntersectionObserverInit;
  };
  itemCSS?: Interpolation;
};

export const Carousel = ({
  carouselApi,
  containerCSS = {},
  children,
  visibilityProps,
  itemCSS = {},
  ...props
}: CarouselProps) => {
  const {
    canScrollNext,
    canScrollPrevious,
    hoverButtons,
    pageOffset,
    refEnd,
    refStart,
    refParent,
    scrollNext,
    scrollPrevious,
    pageButtons,
  } = carouselApi;
  const [visibleIdx, setVisibleIdx] = useState<number | null>(null);
  const [lastVisibleIdx, setLastVisibleIdx] = useState<number | null>(null);

  const { isHovered: hover, hoverProps } = useHover({});

  const childRefs = useRef<RefObject<HTMLDivElement>[]>([]);

  useEffect(() => {
    if (
      visibleIdx !== null &&
      visibleIdx !== lastVisibleIdx &&
      !!visibilityProps?.onVisible
    ) {
      visibilityProps.onVisible(visibleIdx);
      setLastVisibleIdx(visibleIdx);
    }
  }, [visibleIdx, visibilityProps]);

  const getRefPosition = (index: number) => {
    if (!pageOffset && index === (children as ReactNode[]).length - 1)
      return refEnd;
    if (!pageOffset && index === 0) return refStart;
    if (pageButtons) {
      if (!childRefs.current[index])
        childRefs.current[index] = createRef<HTMLDivElement>();
      return childRefs.current[index];
    }
    return null;
  };

  const handlePageClick = (index: number) => {
    const ref = getRefPosition(index);
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (children === null || ((children as ReactNode[]) || []).length === 0)
    return null;

  const childArray = (children as ReactNode[]) || [];

  return (
    <div css={carouselStyle} {...hoverProps} {...props}>
      {hoverButtons && (
        <CarouselButton
          carouselApi={carouselApi}
          direction="previous"
          onClick={scrollPrevious}
          visible={hover && canScrollPrevious}
        />
      )}
      <div
        ref={refParent}
        css={[
          containerStyle,
          pageOffset && containerPageOffsetStyle({ pageOffset }),
          containerCSS,
        ]}
      >
        {pageOffset && (
          <CarouselPageOffset
            carouselApi={carouselApi}
            pageOffsetPosition="start"
            ref={refStart}
            css={startStyle}
          />
        )}
        {childArray.map((child, index) => (
          <CarouselItem
            carouselApi={carouselApi}
            key={index}
            isEnd={index === (children as ReactNode[]).length - 1}
            isStart={index === 0}
            ref={getRefPosition(index)}
            id={`item_${index}`}
            css={itemCSS}
            visibilityProps={{
              onVisible: () => setVisibleIdx(index),
              viewportRef: visibilityProps?.viewportRef,
              observerProps: visibilityProps?.observerProps,
            }}
          >
            {child}
          </CarouselItem>
        ))}
        {pageOffset && (
          <CarouselPageOffset
            carouselApi={carouselApi}
            pageOffsetPosition="end"
            ref={refEnd}
          />
        )}
      </div>
      {hoverButtons && (
        <CarouselButton
          carouselApi={carouselApi}
          direction="next"
          onClick={scrollNext}
          visible={hover && canScrollNext}
        />
      )}
      {pageButtons && childArray.length > 1 && (
        <div css={pageButtonListStyle}>
          {childArray.map((_, index) => (
            <Button
              key={`pageButton_${index}`}
              css={[
                pageButtonStyle,
                visibleIdx === index && activePageButtonStyle,
              ]}
              onClick={() => handlePageClick(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Carousel.displayName = "Carousel";
